import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const BCMAssessmentForm = () => {
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);

  const navigate = useNavigate();
  const token = localStorage.getItem("access_token");

  // --- Fetch phases and saved responses ---
  useEffect(() => {
    if (!token) return navigate("/login");

    const fetchData = async () => {
      try {
        const [bcmRes, savedRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}maturity/bcm/phases/`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${process.env.REACT_APP_API_URL}maturity/assessments/load-progress/`, { headers: { Authorization: `Bearer ${token}` } })
        ]);

        const phases = bcmRes.data;
        const savedResponsesData = savedRes.data.bcm_responses || [];

        const savedMap = {};
        savedResponsesData.forEach(r => {
          const filesArray = r.files || [];
          savedMap[Number(r.bcm_question)] = { ...r, files: filesArray };
        });

        const initialResponses = [];
        phases.forEach(phase => {
          phase.focus_areas.forEach(focus => {
            focus.questions.forEach(q => {
              const saved = savedMap[Number(q.id)];
              initialResponses.push({
                id: saved?.id || null,
                question: Number(q.id),
                answer: saved?.performed
                  ? saved.performed.trim().toLowerCase() === "yes" ? "Yes" : "No"
                  : "",
                evidence: saved?.evidence || "",
                backendFiles: saved?.files || [],
                newFiles: [],
              });
            });
          });
        });

        setQuestions(phases);
        setResponses(initialResponses);
        if (phases.length > 0) setSelectedDomain(phases[0].title);
        setLoading(false);

      } catch (err) {
        console.error("Failed to load BCM data", err);
        setError("Failed to load BCM assessment data.");
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, token]);

  // --- Auto-save handler ---
  const handleAutoSave = async (respObj, newFiles = []) => {
    try {
      const phase = questions.find(p => p.title === selectedDomain);
      const phaseId = phase?.id;
      if (!phaseId) return; // skip until phase is selected

      // --- Step 1: Save the response first ---
      const payload = {
        bcm_question: respObj.question,
        performed: respObj.answer?.toLowerCase() || "",
        evidence: respObj.evidence || "",
      };

      const saveRes = await axios.post(
        `${process.env.REACT_APP_API_URL}maturity/bcm/phase/${phaseId}/save/`,
        { responses: [payload] },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLastSaved(new Date().toLocaleString());

      // --- Step 2: Upload evidence files (if any) ---
      if (newFiles.length > 0) {
        // Use the response IDs returned by the backend
        const backendResp = saveRes?.data?.bcm_responses?.find(
          r => Number(r.bcm_question) === Number(respObj.question)
        );

        if (!backendResp?.id) {
          console.warn("Cannot upload files: BcmResponse not found for question", respObj.question);
          return;
        }

        const responseId = backendResp.id;

        // --- Step 3: Upload each new file ---
        const uploadedFiles = await Promise.all(
          newFiles.map(async file => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("response_id", responseId);
            formData.append("response_type", "bcm");

            try {
              const res = await axios.post(
                `${process.env.REACT_APP_API_URL}maturity/upload-evidence-file/`,
                formData,
                {
                  headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                  },
                }
              );
              return res.data;
            } catch (err) {
              console.error("File upload failed:", err);
              return null;
            }
          })
        );

        // --- Step 4: Update frontend state ---
        setResponses(prev =>
          prev.map(r =>
            r.question === respObj.question
              ? {
                  ...r,
                  backendFiles: [
                    ...(r.backendFiles || []),
                    ...uploadedFiles.filter(Boolean),
                  ],
                  newFiles: [], // clear local new files
                }
              : r
          )
        );
      }
    } catch (err) {
      console.error("Auto-save failed:", err);
    }
  };


  // --- Handlers ---
const handleResponse = (questionId, answer) => {
  setResponses(prev => {
    const updated = prev.map(r =>
      r.question === questionId ? { ...r, answer } : r
    );

    const current = updated.find(r => r.question === questionId);
    if (current) {
      // handleAutoSave already has guard for missing phaseId
      handleAutoSave(current, []);
    }

    return updated;
  });
};

const handleEvidence = (questionId, evidence) => {
  setResponses(prev => {
    const updated = prev.map(r =>
      r.question === questionId ? { ...r, evidence } : r
    );

    const current = updated.find(r => r.question === questionId);
    if (current) {
      // safe call
      handleAutoSave(current, []);
    }

    return updated;
  });
};

const handleFilesSelected = (questionId, fileList) => {
  const newFiles = Array.from(fileList);

  setResponses(prev =>
    prev.map(r =>
      r.question === questionId
        ? { ...r, newFiles: [...(r.newFiles || []), ...newFiles] }
        : r
    )
  );

  // Safely build merged response
  const current =
    responses.find(r => r.question === questionId) || {
      question: questionId,
      answer: "",
      evidence: "",
      backendFiles: [],
      newFiles: [],
    };

  const mergedResp = {
    ...current,
    newFiles: [...(current.newFiles || []), ...newFiles],
  };

  // handleAutoSave will skip if phaseId is not ready
  handleAutoSave(mergedResp, mergedResp.newFiles);
};


  const removeNewFile = (questionId, index) => {
    setResponses(prev =>
      prev.map(r =>
        r.question === questionId
          ? { ...r, newFiles: r.newFiles.filter((_, i) => i !== index) }
          : r
      )
    );
  };

  const removeBackendFile = (questionId, index) => {
    setResponses(prev =>
      prev.map(r =>
        r.question === questionId
          ? { ...r, backendFiles: r.backendFiles.filter((_, i) => i !== index) }
          : r
      )
    );
  };

  const handleSaveProgress = async () => {
    const payload = responses.map(r => ({
      bcm_question: r.question,
      performed: r.answer?.toLowerCase() || '',
      evidence: r.evidence || ''
    }));

    if (!payload.length) {
      alert("No responses to save");
      return;
    }

    const phase = questions.find(p => p.title === selectedDomain);
    const phaseId = phase?.id;

    if (!phaseId) {
      alert("Please select a phase/domain before saving.");
      return;
    }

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}maturity/bcm/phase/${phaseId}/save/`,
        { responses: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setLastSaved(new Date().toLocaleString());
      alert("Progress saved!");
    } catch (err) {
      console.error(err);
      alert("Error saving progress.");
    }
  };


  const getResponsesForPhase = (phaseTitle) => {
    const phase = questions.find(p => p.title === phaseTitle);
    if (!phase) return [];

    const phaseQuestionIds = phase.focus_areas.flatMap(f =>
      f.questions.map(q => q.id)
    );

    return responses.filter(r => phaseQuestionIds.includes(r.question));
  };



  const handleSubmitPhase = async () => {
    const phase = questions.find(p => p.title === selectedDomain);
    const phaseId = phase?.id;

    if (!phaseId) {
      alert("Please select a valid phase/domain before submitting.");
      return;
    }

    const phaseResponses = getResponsesForPhase(selectedDomain);
    if (!phaseResponses.length) {
      alert("No questions found for this phase.");
      return;
    }

    for (let res of phaseResponses) {
      if (!res.answer) {
        alert("All questions in this phase must have an answer.");
        return;
      }
      if (res.answer === "Yes") {
        if (!res.evidence) {
          alert("Questions answered 'Yes' must include evidence text.");
          return;
        }
        const filesCount =
          (res.backendFiles?.length || 0) + (res.newFiles?.length || 0);
        if (filesCount === 0) {
          alert("Questions answered 'Yes' must include at least one file.");
          return;
        }
      }
    }

    const payload = phaseResponses.map(r => ({
      bcm_question: r.question,
      performed: r.answer.toLowerCase(),
      evidence: r.evidence
    }));

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}maturity/bcm/phase/${phaseId}/submit/`,
        { responses: payload },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert(`Phase "${selectedDomain}" submitted successfully!`);
    } catch (err) {
      console.error(err);
      alert("Submission for this phase failed!");
    }
  };



  if (loading) return <div className="p-4 text-center">Loading questions...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;

  const getPhaseProgress = (phase) => {
    if (!phase) return { total: 0, answered: 0, percentage: 0 };

    const allQs = phase.focus_areas.flatMap(f => f.questions);
    const total = allQs.length;

    const answered = allQs.filter(q => {
      const r = responses.find(res => res.question === q.id);
      if (!r?.answer) return false;
      if (r.answer === "No") return true;
      if (r.answer === "Yes") {
        return (
          r.evidence?.trim() &&
          ((r.backendFiles?.length || 0) + (r.newFiles?.length || 0) > 0)
        );
      }
      return false;
    }).length;

    const percentage = total ? Math.round((answered / total) * 100) : 0;
    return { total, answered, percentage };
  };



  return (
    <div className="max-w-5xl mx-auto px-4 py-8 mb-16 overflow-visible relative">
      <h1 className="text-3xl font-bold mb-6 text-center text-amber-700">BCM Assessment Form</h1>
      {/* Phase buttons */}
      <div className="sticky top-0 z-50 bg-white shadow-md p-2 flex flex-wrap gap-2 justify-center mb-4">
        {questions.map(phase => {
          const total = phase.focus_areas.reduce((sum, f) => sum + f.questions.length, 0);
          const answered = phase.focus_areas.reduce((sum, f) =>
            sum + f.questions.filter(q => {
              const r = responses.find(res => res.question === q.id);
              return r?.answer && (r.answer === "No" || (r.answer === "Yes" && r.evidence && ((r.backendFiles?.length || 0) + (r.newFiles?.length || 0) > 0)));
            }).length
          , 0);

          const phaseLabel = `${phase.code} | ${phase.title}`;

          return (
            <button
              key={phase.id}
              onClick={() => {
                setSelectedDomain(phase.title);
                window.scrollTo({ top: 0, behavior: 'smooth' }); // scroll to top
              }}
              className={`px-4 py-2 rounded-lg border text-sm ${selectedDomain === phase.title ? 'bg-amber-100 border-amber-600' : 'bg-white border-gray-300'}`}
            >
              {phaseLabel} ({answered}/{total})
            </button>
          );
        })}
      </div>

      {/* Phase Progress Bar */}
      {selectedDomain && (() => {
        const phase = questions.find(p => p.title === selectedDomain);
        const { total, answered, percentage } = getPhaseProgress(phase);

        const getBarColor = () => {
          if (percentage < 40) return "bg-red-500";
          if (percentage < 70) return "bg-yellow-500";
          return "bg-green-600";
        };

        return (
          <div className="sticky top-[56px] z-50 bg-white shadow-sm pb-3 px-4 mb-6">
            <p className="text-sm text-gray-700 mb-1 text-center">
              Progress for <strong>{phase.code} | {phase.title}</strong>: {answered} of {total} answered ({percentage}%)
            </p>
            <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className={`${getBarColor()} h-4 transition-all duration-500`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>
        );
      })()}

      {/* Questions */}
      {selectedDomain && questions.find(p => p.title === selectedDomain)?.focus_areas.map(focus => (
        <div key={focus.id}>
          <h2 className="text-xl font-semibold mt-6 mb-2">{focus.title}</h2>
          {focus.questions.map(q => {
            const current = responses.find(r => r.question === q.id) || { backendFiles: [], newFiles: [], answer: '', evidence: '' };
            return (
              <div key={q.id} className="mb-8 bg-white p-4 rounded-lg shadow hover:shadow-xl transition">
                <label className="block font-medium mb-2">Question: {q.request}</label>

                <select
                  value={current.answer || ''}
                  onChange={e => handleResponse(q.id, e.target.value)}
                  className={`w-full border rounded-md p-2 mb-2 focus:outline-none focus:ring-2 appearance-none ${current.answer === 'Yes' ? 'bg-amber-100 border-amber-400' : current.answer === 'No' ? 'bg-red-100 border-red-400' : 'bg-white border-gray-300'}`}
                >
                  <option value="">Select</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>

                {current.answer === "Yes" && (
                  <>
                    <input
                      type="text"
                      placeholder="Add a comment or note"
                      value={current.evidence || ''}
                      onChange={e => handleEvidence(q.id, e.target.value)}
                      className="w-full border border-gray-300 rounded-md p-2 mb-2"
                    />
                    <input
                      type="file"
                      multiple
                      onChange={e => handleFilesSelected(q.id, e.target.files)}
                      className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:border file:rounded-md file:text-sm file:font-semibold file:bg-amber-600 file:text-white hover:file:bg-amber-700 mb-2"
                    />

                    {/* Existing backend files */}
                    {current.backendFiles?.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {current.backendFiles.map((file, index) => (
                          <div key={index} className="flex justify-between bg-gray-100 p-2 rounded-md shadow-sm items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{(file.name || file.file_url).toLowerCase().endsWith('.pdf') ? '🗎' : '📄'}</span>
                              <div>
                                <p className="text-sm">{file.name || file.file_url.split('/').pop()}</p>
                                <p className="text-xs text-gray-500">{file.size ? `${(file.size / 1024).toFixed(1)} KB` : ''}</p>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <a href={file.file_url || '#'} download={file.name} className="text-blue-600 text-sm hover:underline" target="_blank" rel="noopener noreferrer">Download</a>
                              <button type="button" className="text-red-500 text-sm hover:underline" onClick={() => removeBackendFile(q.id, index)}>Delete</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Newly added files */}
                    {current.newFiles?.length > 0 && (
                      <div className="mt-2 space-y-2">
                        {current.newFiles.map((file, index) => (
                          <div key={index} className="flex justify-between bg-gray-200 p-2 rounded-md shadow-sm items-center">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{file.name.toLowerCase().endsWith('.pdf') ? '🗎' : '📄'}</span>
                              <p className="text-sm">{file.name}</p>
                            </div>
                            <button type="button" className="text-red-500 text-sm hover:underline" onClick={() => removeNewFile(q.id, index)}>Remove</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Action buttons */}
      <div className="flex gap-4 justify-center mt-6">
        <button
          type="button"
          onClick={handleSubmitPhase}
          className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded transition"
        >
          Submit This Phase
        </button>

        <button
          type="button"
          onClick={handleSaveProgress}
          className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded transition"
        >
          Save Progress
        </button>

        <button
          type="button"
          onClick={() => navigate('/bcm/dashboard')}
          className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2 rounded transition"
        >
          Go to Dashboard
        </button>
      </div>
      {lastSaved && <p className="text-sm text-gray-600 mt-2 text-center">Last saved: {lastSaved}</p>}
    </div>
  );
};

export default BCMAssessmentForm;
